import { DATE_FORMAT } from '@/constants'
import { CtCatalogue, CtDraft, CtEvent, CtOrder, CtProduct, CtStockItem } from '@/models/interfaces/content-type'
import { OrderProduct } from '@/models/interfaces/order'
import { Status } from '@/models/status'
import useIO from '@/pages/composables/useIO'
import Directr, { ContentType } from '@/services/directr'
import { errorToReadableString, validateAddress } from '@/utils/error'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import { AxiosError } from 'axios'
import moment from 'moment'
import { QNotifyCreateOptions, QNotifyUpdateOptions } from 'quasar'
import { Ref, computed, ref } from 'vue'
import { Store } from 'vuex'

class IssueTracker {
  public loading: boolean

  private _events_dict: Map<UUID, Array<CtEvent>>

  private _issues: Ref<Array<CtOrder>>
  private _drafts: Ref<Array<CtDraft>>
  private _catalogues: Ref<Array<CtCatalogue>>

  private $store: Store<any>
  private $io: ReturnType<typeof useIO>
  private $notifier: ((opts: string | QNotifyCreateOptions) => (props?: QNotifyUpdateOptions | undefined) => void)
  public $directr: Directr


  constructor ($store: Store<any>, $notify) {
    this.loading = false

    this._events_dict = new Map()

    this.$store = $store
    this.$notifier = $notify
    this.$directr = $store.getters['Services/Directr']

    this._issues = ref([])
    this._drafts = ref([])
    this._catalogues = ref([])

    this.$io = useIO($store)
  }

  async init ($store?: Store<any>) {
    if ($store) {
      this.$store = $store
      this.$directr = $store.getters['Services/Directr']
    }

    await this.fetchCatalogues()

    await this.issues.load()
    await this.drafts.load()

    this.$io.listen('create/draft:order', ({message, payload}) => {
      // console.debug('create/draft:order', payload)
      if (payload?.sys) this.drafts.add(payload)
    })
    this.$io.listen('update/draft:order', ({message, payload}) => {
      // console.debug('update/draft:order', payload)
      if (payload?.sys) this.drafts.add(payload)
    })
    this.$io.listen('delete/draft:order', ({message, payload}) => {
      // console.debug('delete/draft:order', payload)
      if (payload) this.drafts.remove(payload)
    })
    this.$io.listen('update/order', ({message, payload}) => {
      // console.debug('error/order', payload)
      if (payload?.status?.includes('failed')) this.issues.add(payload)
      else this.issues.remove(payload)
    })
  }

  private $notify (message: string, type: 'positive' | 'negative' = 'positive', caption?: string) {
    return this.$notifier({ type, message, caption, classes: `custom-notify ${type === 'positive' ? 'success' : 'error'}` })
  }

  get dict () {
    return {
      draft: computed({
        get: () => Object.fromEntries((this._drafts.value || []).map(d => [d.sys.id, d])),
        set: (value) => {
          this._drafts.value = Object.values(value)
          // this._drafts.value.splice(this.drafts.index(value.sys.id), 1, value)
        }
      }),
      issue: computed({
        get: () => Object.fromEntries((this._issues.value || []).map(d => [d.sys.id, d])),
        set: (value) => {
          this._issues.value = Object.values(value)
          // this._issues.value.splice(this.drafts.index(value.sys.id), 1, value)
        }
      }),
      catalogue: Object.fromEntries((this._catalogues.value || []).map(c => [c.sys.id, c]))
    }
  }

  async fetchCatalogues () {
    this._catalogues.value = (await this.$directr.get('catalogue', [{ select: ['id','name','service'] }])).items || []
  }


  // ---- Issues

  get issues () {
    return {
      get: (id) => this._issues.value?.find(d => d.sys.id === id),
      add: (order: CtOrder) => {
        const index = this._issues.value.findIndex(d => d.sys.id === order?.sys?.id)
        if (index >= 0) this._issues.value.splice(index, 1, order)
        else this._issues.value.push(order)
      },
      remove: (order: string | CtOrder) => {
        const index = this._issues.value.findIndex(d => d.sys.id === (typeof order === 'object' ? order?.sys?.id : order))
        console.debug(Date.now(), order, index)
        if (index >= 0) {
          // console.log('removing', Date.now(), order)
          this._issues.value.splice(index, 1)
        }
      },
      index: (id) => this._issues.value?.findIndex(d => d.sys.id === id),
      total: (this._issues.value || []).length,
      value: this._issues.value || [],
      // list: () => this._issues || [],
      load: this.fetchIssues.bind(this),
      resolve: this.resolveIssue.bind(this),
      addNote: (id: UUID, note: string) => this.$directr.order.patchOperationalNote(id, note).then(() => this.$notify('Note added')).catch((error) => this.$notify('There was an error adding the note', 'negative') && console.error(error)),
      resetOrder: (id: UUID) => this.$directr.order.reset(id).then(() => this.$notify('Order reset')).catch((error) => this.$notify('There was an error while resetting the order', 'negative') && console.error(error)),
      product: {
        update: (orderId: UUID, productIndex: number, product: CtProduct | CtStockItem) => {
          if (!orderId || productIndex < 0 || !product) return

          const order = this._issues.value?.find(d => d.sys.id === orderId)
          if (!order) return

          if (!order.products[productIndex]) return
          order.products[productIndex] = {
            title: product.title || order.products[productIndex].title,
            sys: product.sys,
            sku: product.sku as string,
            ean: product.ean as any,
            quantity: order.products[productIndex].quantity || 1
          } as any // cast due to OrderProduct being completely required by default
        }
      }
    }
  }

  get validate () {
    return {
      address: (address) => {
        return !Object.values(validateAddress(address)).some(n => n?.error)
      },
      products: (items) => {
        return items.every(p => !!p.sys || ((p.sku || p.ean) && parseInt(p.quantity) > 0))
      }
    }
  }

  private async fetchIssues () {
    if (!this.$directr) return console.warn('this.$directr was undefined')
    this._issues.value = await this.$directr
      .get('orders', [
        { select: ['reference', 'id', 'status', 'status_info', 'tags', 'shipping_address', 'catalogue', 'products', 'fulfillments', 'shipments'] },
        { field: 'status', method: 'in', match: 'failed' }, { field: 'status', method: 'nin', match: 'resolved' },
        { field: 'status', method: 'nin', match: 'final' }, { field: 'status', method: 'nin', match: 'cancelled' },
        { field: 'sys.updated_at', method: 'gte', match: `${moment().subtract(6, 'months').startOf('day').format(DATE_FORMAT)}` }
      ])
      .then((res) => res.items || [])
  }

  private async resolveIssue (id: UUID, status?: Status) {
    try {
      // fetch the task from the list (instead of passing data arguments)
      const orderIndex = this.issues.value.findIndex(i => i.sys.id === id)

      if (orderIndex < 0) throw Error(`Could not find order by id "${id}"`)
      const order = this.issues.value[orderIndex]
      const { shipping_address, products } = order

      if (!this.validate.address(shipping_address) || !this.validate.products(products))
        throw Error('Validation failed')

      // if valid, perform the update & call the issueResolved ping endpoint (TODO: make optional by user interaction)
      const updated = await this.$directr.order.update(id, { shipping_address, products })
      const pinged = await this.$directr.put('order.issue.resolve', id, {status: [status]}, {})
      // console.log('order updated', updated, pinged)

      this._issues.value?.splice(orderIndex, 1)
      this.$notify('Order issue marked as resolved')
    } catch (error) {
      console.error(error)
      this.$notify('There was an error while updating the order', 'negative')
    }
  }


  // ---- Drafts

  get drafts () {
    return {
      get: (id) => this._drafts.value?.find(d => d.sys.id === id),
      add: (draft: CtDraft) => {
        const index = this._drafts.value.findIndex(d => d.sys.id === draft?.sys?.id)
        if (index >= 0) this._drafts.value.splice(index, 1, draft)
        else this._drafts.value.push(draft)
      },
      remove: (draft: string | CtDraft) => {
        const index = this._drafts.value.findIndex(d => d.sys.id === (typeof draft === 'object' ? draft?.sys?.id : draft))
        if (index >= 0) this._drafts.value.splice(index, 1)
      },
      index: (id) => this._drafts.value?.findIndex(d => d.sys.id === id),
      total: (this._drafts.value || []).length,
      value: this._drafts || [],
      // list: () => this._drafts || [],
      load: this.fetchDrafts.bind(this),
      commit: this.commitDraft.bind(this),
      ignore: this.ignoreDraft.bind(this),
      product: {
        update: (draftId: UUID, productIndex: number, product: CtProduct | CtStockItem) => {
          if (!draftId || productIndex < 0 || !product) return

          const draft = this._drafts.value?.find(d => d.sys.id === draftId)
          if (!draft) return

          const orderData = draft.raw as CtOrder<Partial<OrderProduct>> | undefined
          if (!orderData) return

          if (!orderData.products[productIndex]) return
          orderData.products[productIndex] = {
            title: product.title || orderData.products[productIndex].title,
            sys: product.sys,
            sku: product.sku as string,
            ean: product.ean as any,
            quantity: orderData.products[productIndex].quantity || 1
          }
        }
      }
    }
  }

  private async fetchDrafts () {
    if (!this.$directr) return console.warn('this.$directr was undefined')
    this._drafts.value = await this.$directr
      .get('draft', [{ field: 'type', method: 'is', match: 'orders' }, { field: 'status', method: 'nin', match: 'paused' }])
      .then((res) => res.items || [])
  }

  private async ignoreDraft (id: UUID) {
    try {
      const index = this._drafts.value.findIndex(f => f.sys?.id === id)
      if (index < 0) throw Error(`Could not find draft by id "${id}"`)

      const paused = await this.$directr.put('draft', id, { status: [Status.Paused] })

      this._drafts?.value.splice(index, 1)
      this.$notify('Draft ignored')
    } catch (error) {
      console.error(error)
      this.$notify('There was an issue while marking the draft as "ignored"', 'negative')
    }
  }

  private async commitDraft (id: UUID, {shipping_address, products}) {
    try {
      const index = this._drafts.value.findIndex(f => f.sys?.id === id)
      if (index < 0) throw Error(`Could not find draft by id "${id}"`)

      const draft = this._drafts.value[index]
      if (!draft.raw) throw Error(`Draft ${id} did not have raw data`)

      // perform validation
      if (shipping_address && !this.validate.address(shipping_address)) throw Error('Address was invalid')
      if (products && !this.validate.products(products)) throw Error('Products were invalid')

      const order = { ...draft.raw, shipping_address, products }

      const created = await this.$directr.put(ContentType.Order, (order as any).id, order)

      // delete the draft (should already happen when order is created successfully)
      // const deleted = await this.$directr.delete('draft', id).catch(console.error)

      // complete the task
      this._drafts.value?.splice(index, 1)
      this.$notify('Draft processed')
    } catch (error: any) {
      console.error(error?.response?.data || error)
      this.$notify('There was an issue processing the draft', 'negative', errorToReadableString(error instanceof AxiosError ? (error.response?.data || error) : (error?.data || error)))
    }
  }


}

export default IssueTracker
